/**
 * Radar de convocatorias — Sprint 2 (listado) + Sprint 3 (filtros) + Sprint 4 (matching).
 *
 * Sprint 4 agrega:
 * - `municipality` query param en el fetch → backend devuelve score+reasons por card.
 * - `MatchBadge` prominente en cada tarjeta (alto/medio/bajo/muy bajo).
 * - Lista "Por qué te aplica" (razones humanas) expandida en la card.
 * - Orden por relevancia DESC (lo hace el backend).
 *
 * Municipio activo: hardcoded a Neira 17486 (placeholder del AppShell).
 * Sprint 7 lo reemplaza con un Combobox real.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleCheck, Info } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { BulkActionsBar } from "@/components/BulkActionsBar";
import { CupoExpedientesNota } from "@/components/CupoExpedientesNota";
import { EligibilityBadge } from "@/components/EligibilityBadge";
import { EmptyState } from "@/components/EmptyState";
import { MatchBadge } from "@/components/MatchBadge";
import { OnboardingTour, type TourStep } from "@/components/OnboardingTour";
import { OpportunityActionButtons } from "@/components/OpportunityActionButtons";
import { EMPTY_FILTERS, RadarFilters, type RadarFilterState } from "@/components/RadarFilters";
import { RadarTabs, type RadarTab } from "@/components/RadarTabs";
import { UrgencyBadge } from "@/components/UrgencyBadge";
import { UrlHealthBadge } from "@/components/UrlHealthBadge";
import {
  ApiError,
  createProject,
  fetchOpportunities,
  recordOpportunityView,
  type OpportunityDTO,
} from "@/lib/api";
import { track } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";
import { textoCupoAgotado } from "@/lib/cupo-expedientes";
import { formatDateEs } from "@/lib/dates";
import { RADAR_CURATION_ACTIONS_ENABLED } from "@/lib/features";
import { myPlanQueryKey, useActiveProjectsQuota } from "@/lib/hooks/useActiveProjectsQuota";
import { useActiveMunicipalityDane } from "@/lib/municipality";
import { formatCOP } from "@/lib/utils";

/**
 * Pasos del tour guiado del Radar (Sprint 8.5).
 *
 * Se muestra una vez por usuario tras completar onboarding. La copia
 * habla en segunda persona (tú) — coincidiendo con la convención de
 * idioma del proyecto (CLAUDE.md §9).
 *
 * Sprint 10 · 5: el paso "actions" (favoritar/descartar) y la mención
 * a destacadas/favoritas/descartadas en el paso "filters" se quitaron
 * cuando esas acciones se movieron a V2 (ver `lib/features.ts`).
 *
 * Sprint 19 UX-1 (mayo 21, 2026) eliminó el chip "Destacadas" y el borde
 * resaltado de `is_featured`; el tour no debe mencionarlos (issue #540,
 * cubierto por `Radar.test.tsx` y `e2e/tests/sweep/radar.spec.ts`).
 *
 * Si una card aún no se renderizó cuando se activa un step
 * contextual, el componente OnboardingTour lo salta automáticamente.
 */
const RADAR_TOUR_STEPS_BASE: TourStep[] = [
  {
    id: "welcome",
    title: "Bienvenido a Kaptar",
    body: "Este es tu Radar — el corazón de la plataforma. Aquí ves todas las convocatorias activas que aplican a tu municipio, ordenadas por relevancia. Vamos a un recorrido de 30 segundos.",
  },
  {
    id: "filters",
    target: "[data-tour-id='radar-filters']",
    placement: "bottom",
    title: "Filtros del Radar",
    body: RADAR_CURATION_ACTIONS_ENABLED
      ? "Refina por sector (agua, vías, conectividad…), urgencia (cierran en 7 o 30 días) o búsqueda libre. También puedes ver solo tus favoritas o restaurar las que descartaste."
      : "Refina por sector (agua, vías, conectividad…), urgencia (cierran en 7 o 30 días) o búsqueda libre.",
  },
  {
    id: "card",
    target: "[data-tour-id='radar-card']",
    placement: "top",
    title: "Cada convocatoria, en una tarjeta",
    body: "Verás el porcentaje de relevancia con sus razones (qué tan bien encaja con tu municipio), la fuente oficial, la urgencia del cierre y si el enlace está activo.",
  },
];

const RADAR_TOUR_STEP_ACTIONS: TourStep = {
  id: "actions",
  target: "[data-tour-id='card-actions']",
  placement: "left",
  title: "Marca y descarta",
  body: "Con ♥ guardas las que te interesan para revisar después. Con ✘ las descartas y dejas de verlas. Ambas se sincronizan con tu cuenta — mismas favoritas en cualquier dispositivo.",
};

const RADAR_TOUR_STEPS: TourStep[] = RADAR_CURATION_ACTIONS_ENABLED
  ? [...RADAR_TOUR_STEPS_BASE, RADAR_TOUR_STEP_ACTIONS]
  : RADAR_TOUR_STEPS_BASE;

/**
 * Tarjetas por pagina del Radar. Issue #733: el backend ordena por puntaje
 * todas las activas antes de cortar, asi que lo que queda despues de la
 * primera pagina son las de menor puntaje; "Ver mas" pide la siguiente con
 * `offset` para que el contador nunca prometa tarjetas inalcanzables.
 */
const RADAR_PAGE_SIZE = 100;

export function Radar() {
  const { user } = useAuth();
  const activeDane = useActiveMunicipalityDane();
  // Issue #614: oportunidades perdidas, alertas y el briefing de la ficha
  // enlazan a `/radar?sector=X`. El filtro inicial sale de la URL; despues
  // el usuario lo cambia desde los controles como siempre.
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<RadarFilterState>(() => ({
    ...EMPTY_FILTERS,
    sector: searchParams.get("sector") ?? "",
  }));
  // V2-S6.1: pestaña activa del Radar (Nacional / Internacional).
  const [tab, setTab] = useState<RadarTab>("national");
  // Sprint 9 · 3: multi-select state. Set por id para O(1) lookup.
  // Lo limpiamos cuando cambian los filtros (la card seleccionada
  // puede salir de la página) o cuando el usuario completa una acción
  // bulk.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const {
    data,
    isLoading,
    isError,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteQuery({
    // queryKey incluye los filtros + municipio + tab → TanStack Query cachea por combinación.
    queryKey: ["opportunities", filters, activeDane, tab],
    queryFn: ({ pageParam }) =>
      fetchOpportunities({
        limit: RADAR_PAGE_SIZE,
        offset: pageParam,
        search: filters.search || undefined,
        sector: filters.sector || undefined,
        max_days_to_close: filters.maxDaysToClose,
        municipality: activeDane,
        favorited_only: filters.onlyFavorited || undefined,
        include_dismissed: filters.includeDismissed || undefined,
        origin: tab,
      }),
    initialPageParam: 0,
    // La siguiente pagina arranca donde terminaron las pedidas. Una pagina
    // vacia corta aunque el total diga mas (evita pedir en bucle).
    getNextPageParam: (lastPage, pages) => {
      const pedidas = pages.reduce((n, page) => n + page.items.length, 0);
      return lastPage.items.length > 0 && pedidas < lastPage.total ? pedidas : undefined;
    },
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return count < 2;
    },
  });
  // Sprint 19 UX-1 (mayo 21, 2026): el filtro "solo destacadas" se
  // eliminó junto con el concepto de Featured. El listado es el que
  // viene del backend, ordenado por match_score + closes_at.
  // Si una convocatoria nueva corre el orden entre una pagina y otra, la
  // que se repite se pinta una sola vez (el id es la key de la tarjeta).
  const opportunities = data
    ? Array.from(new Map(data.pages.flatMap((page) => page.items).map((o) => [o.id, o])).values())
    : undefined;
  const total = data?.pages[data.pages.length - 1]?.total;
  // Filtros que reducen el listado (búsqueda, sector, urgencia). Deciden si
  // un listado vacío es "no coincide" o "no hay activas".
  const hasNarrowingFilters =
    filters.search !== "" || filters.sector !== "" || filters.maxDaysToClose !== undefined;
  // Issue #877: con "Mostrar descartadas" apagado el backend quita las
  // descartadas antes de contar, así que `total` no dice si el vacío lo
  // causó el usuario al descartar. Solo cuando el listado sale vacío se
  // pregunta lo mismo con las descartadas incluidas (1 fila basta). La key
  // cuelga de "opportunities" para que descartar/restaurar la invalide.
  const descartadasOcultas = useQuery({
    queryKey: ["opportunities", "descartadas-ocultas", filters, activeDane, tab],
    queryFn: () =>
      fetchOpportunities({
        limit: 1,
        search: filters.search || undefined,
        sector: filters.sector || undefined,
        max_days_to_close: filters.maxDaysToClose,
        municipality: activeDane,
        favorited_only: filters.onlyFavorited || undefined,
        include_dismissed: true,
        origin: tab,
      }),
    enabled: opportunities?.length === 0 && !filters.includeDismissed,
    retry: false,
  });
  const todasDescartadas = (descartadasOcultas.data?.total ?? 0) > 0;
  // Issue #541: el motor no encontró ninguna razón para ninguna tarjeta
  // (típico de un municipio categoría 1-3 no PDET). El orden de score cae
  // entero al desempate por fecha de cierre, y "ordenadas por relevancia"
  // dejaría de ser cierto: se dice en la página.
  const sinSenales =
    !!opportunities &&
    opportunities.length > 0 &&
    opportunities.every((o) => o.match !== null && o.match.reasons.length === 0);

  // Helpers de selección. Se memoizan implícitamente como callbacks
  // estables porque dependen solo de setSelectedIds (que React garantiza
  // estable). useCallback explícito sería overkill — son inline.
  function toggleSelection(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function clearSelection() {
    setSelectedIds(new Set());
  }

  // V2-S6.1: cambio de pestana activa + evento de telemetria.
  function handleTabChange(next: RadarTab) {
    setTab(next);
    track({ event_name: "radar_tab_switched", properties: { tab: next } });
  }

  // Si los filtros cambian, las cards seleccionadas pueden salir de la
  // página actual — limpia para evitar que el bulk afecte items que el
  // usuario ya no ve. Trade-off: pierde selección al filtrar; el costo
  // es menor que actuar sobre cosas no visibles.
  const filtersFingerprint = JSON.stringify(filters);
  const [prevFingerprint, setPrevFingerprint] = useState(filtersFingerprint);
  if (prevFingerprint !== filtersFingerprint) {
    setPrevFingerprint(filtersFingerprint);
    setSelectedIds(new Set());
  }

  // Telemetría: registramos cuándo el usuario aplica un filtro. Saltamos
  // el primer render (el estado inicial vacío no es "filtrar"). No logueamos
  // el texto de búsqueda — solo si hay búsqueda activa — para no meter PII
  // ni ruido en las properties.
  const isInitialFilterRender = useRef(true);
  useEffect(() => {
    if (isInitialFilterRender.current) {
      isInitialFilterRender.current = false;
      return;
    }
    track({
      event_name: "radar_filtered",
      properties: {
        sector: filters.sector || null,
        max_days_to_close: filters.maxDaysToClose ?? null,
        has_search: Boolean(filters.search),
      },
    });
    // Disparamos solo cuando cambia el fingerprint de filtros, no por
    // cambios de identidad de `filters`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersFingerprint]);

  // Cupo de expedientes del plan (issue #568). Falla abierto: si el plan
  // no cargó, `isExhausted` es false y manda el 402 del backend.
  const cupoExpedientes = useActiveProjectsQuota();

  // Map id → opp para que BulkActionsBar pueda crear expediente con
  // los datos de la card seleccionada.
  const opportunitiesById = new Map((opportunities ?? []).map((o) => [o.id, o]));
  // Filtrar selectedIds que ya no están en la página actual (ej. tras
  // cambio de filtros — defense en profundidad junto al clearSelection).
  const visibleSelectedIds = Array.from(selectedIds).filter((id) => opportunitiesById.has(id));

  // El tour espera la primera carga de convocatorias (con datos o vacía):
  // "Paso N de M" se mide al arrancar, y si arranca antes de que lleguen
  // las tarjetas dice "Paso 1 de 2" y luego "de 4" (#840). Se marca una
  // sola vez: cambiar filtros o pestaña vuelve a cargar, y desmontar el
  // tour a mitad de recorrido lo reiniciaría.
  const [tourReady, setTourReady] = useState(false);
  if (!tourReady && !isLoading) setTourReady(true);

  return (
    <div className="mx-auto max-w-6xl">
      {/* Tour guiado de primera sesión — solo se muestra una vez por usuario.
          Se monta dentro del Radar (no del AppShell) porque los pasos
          apuntan a elementos específicos de esta página: filtros + cards.
          Sin convocatorias, los pasos que apuntan a cards se auto-saltan. */}
      {/* La storageKey incluye el `user.id` para que el flag de
          "tour visto" sea por usuario, no por browser. Caso real:
          un equipo compartido o un mismo browser con múltiples
          identidades (delete + re-invite recreando el user) NO debe
          ocultar el tour al user nuevo solo porque el viejo lo cerró.
          Si user es null por algún motivo (Radar es ProtectedRoute,
          no debería pasar), usamos un fallback "anon" — el tour se
          muestra una vez por browser anónimo y se persiste. */}
      {tourReady && (
        <OnboardingTour
          storageKey={`kaptar.tour.radar.v1.${user?.id ?? "anon"}`}
          steps={RADAR_TOUR_STEPS}
        />
      )}

      <header className="mb-6" data-tour-id="radar-header">
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
          Radar de oportunidades
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Convocatorias activas agregadas desde las fuentes oficiales.
        </p>
      </header>

      <RadarTabs value={tab} onChange={handleTabChange} />

      <div data-tour-id="radar-filters">
        <RadarFilters value={filters} onChange={setFilters} totalResults={total} />
      </div>

      {/* V2-S6.1: banner de honestidad para la pestana internacional.
          Se muestra siempre que la tab activa sea internacional,
          independientemente del estado de carga. */}
      {tab === "international" && (
        <div
          data-testid="intl-eligibility-banner"
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          La cooperacion internacional rara vez se aplica directo — te marcamos
          <strong> quien puede aplicar</strong> en cada convocatoria.
        </div>
      )}

      {isLoading && <RadarSkeleton />}

      {/* Si lo que falla es "Ver más", las tarjetas ya pintadas siguen y el
          aviso va junto al botón, no aquí. */}
      {isError && !isFetchNextPageError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-800">
          <p className="font-semibold">Error cargando convocatorias.</p>
          <p className="mt-1">{error instanceof Error ? error.message : "Error desconocido."}</p>
        </div>
      )}

      {/* Issue #539: el chip "♥ Favoritas" también acota el listado. Con 0
          favoritas el vacío correcto es "no tienes favoritas", nunca "sin
          convocatorias activas" (hay activas; lo que falta es marcarlas).
          Issue #877: con "Mostrar descartadas" apagado, las descartadas sí
          acotan. Mientras se averigua si el vacío es por ellas no se pinta
          nada: un "sin convocatorias activas" de paso sería falso. */}
      {opportunities &&
        opportunities.length === 0 &&
        !descartadasOcultas.isLoading &&
        (todasDescartadas ? (
          <EmptyState
            variant="no-results"
            title={
              hasNarrowingFilters
                ? "Descartaste todas las convocatorias que coinciden con los filtros"
                : "Descartaste todas las convocatorias activas de esta pestaña"
            }
            description="Siguen activas: solo están ocultas para ti."
            hint="Usa Mostrar descartadas para volver a verlas y restaurar las que te sirvan."
          />
        ) : filters.onlyFavorited && !hasNarrowingFilters ? (
          <EmptyState
            variant="no-results"
            title="Todavía no tienes favoritas"
            description="Marca con ♥ las convocatorias que quieras seguir y aparecerán aquí."
            hint="Quita el filtro Favoritas para volver a ver todas las activas."
          />
        ) : hasNarrowingFilters ? (
          <EmptyState
            variant="no-results"
            title="Ninguna convocatoria coincide con los filtros"
            description="Prueba a relajar los filtros: ampliar el rango de cierre, cambiar el sector, o limpiar la búsqueda."
          />
        ) : (
          <EmptyState
            variant="coming-soon"
            title="Sin convocatorias activas en este momento"
            description="Estamos monitoreando 8+ portales oficiales (SGR, MinTIC, MinEnergía, APC, MinCit). Apenas se publique algo, aparecerá aquí."
            hint="También recibirás alerta por correo cuando aplique a tu municipio."
          />
        ))}

      {opportunities && opportunities.length > 0 && (
        <>
          {/* Cupo agotado: un aviso para la página, no uno por tarjeta
              (issue #568). Va arriba del grid para que se lea antes de
              intentar crear, no después del botón deshabilitado. */}
          {cupoExpedientes.isExhausted && cupoExpedientes.quota && (
            <CupoExpedientesNota
              className="mb-4"
              mensaje={textoCupoAgotado(cupoExpedientes.tier, cupoExpedientes.quota.limit)}
            />
          )}
          {sinSenales && (
            <div
              role="status"
              data-testid="radar-sin-senales"
              className="mb-4 flex items-start gap-2 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-900"
            >
              <Info aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" />
              <p>
                No encontramos criterios que prioricen estas convocatorias para tu municipio (PDET,
                región, déficit por sector o experiencia en SGR), así que las ordenamos por fecha de
                cierre. Revisa en cada una quién puede aplicar.
              </p>
            </div>
          )}
          {/*
            Sprint 19 UX-0 (mayo 21, 2026): se eliminó UrlHealthLegend
            de acá — los labels del UrlHealthBadge en cada card ya son
            auto-explicativos. La leyenda era ruido visual.
          */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {opportunities.map((o, idx) => (
              <OpportunityCard
                key={o.id}
                opp={o}
                /* data-tour-id en la primera card únicamente — el tour
                   apunta solo ahí para evitar tooltips solapados. */
                tourTarget={idx === 0}
                isSelected={selectedIds.has(o.id)}
                cupoAgotado={cupoExpedientes.isExhausted}
                onToggleSelect={
                  RADAR_CURATION_ACTIONS_ENABLED ? () => toggleSelection(o.id) : undefined
                }
              />
            ))}
          </div>
          {hasNextPage && total !== undefined && (
            <div
              data-testid="radar-paginacion"
              className="mt-6 flex flex-col items-center gap-2 text-sm text-slate-600"
            >
              <p>
                Ves {opportunities.length} de {total} convocatorias.
              </p>
              <button
                type="button"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
                className="rounded-md border border-brand-700 bg-white px-4 py-2 text-sm font-semibold text-brand-700 hover:bg-brand-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700 disabled:cursor-wait disabled:opacity-60"
              >
                {isFetchingNextPage ? "Cargando convocatorias…" : "Ver más convocatorias"}
              </button>
              {isFetchNextPageError && !isFetchingNextPage && (
                <p role="alert" className="text-red-700">
                  No pudimos cargar más convocatorias. Revisa tu conexión y vuelve a pulsar «Ver más
                  convocatorias».
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* Barra de acciones bulk — sticky bottom cuando hay selección.
          Solo se renderiza si hay DANE activo (anónimo no puede
          favoritar/descartar) y la curaduría está habilitada (V2). */}
      {RADAR_CURATION_ACTIONS_ENABLED && activeDane && visibleSelectedIds.length > 0 && (
        <BulkActionsBar
          cupoAgotado={cupoExpedientes.isExhausted}
          selectedIds={visibleSelectedIds}
          opportunitiesById={opportunitiesById}
          dane={activeDane}
          onClear={clearSelection}
        />
      )}
    </div>
  );
}

function OpportunityCard({
  opp,
  tourTarget = false,
  isSelected = false,
  cupoAgotado = false,
  onToggleSelect,
}: {
  opp: OpportunityDTO;
  tourTarget?: boolean;
  isSelected?: boolean;
  /** El plan ya no admite otro expediente activo (issue #568). */
  cupoAgotado?: boolean;
  onToggleSelect?: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const activeDane = useActiveMunicipalityDane();
  const registrarVista = () => {
    // Telemetría de producto (analytics_events) — agregada.
    track({
      event_name: "opportunity_viewed",
      properties: {
        opportunity_id: opp.id,
        source_code: opp.source.code,
      },
    });
    // Ledger legal de descubrimiento (discovery_events, hash-chain
    // con valor para la comisión — CLAUDE.md regla 8). Best-effort:
    // no bloquea la navegación al original ni muestra error si falla.
    void recordOpportunityView({
      opportunityId: opp.id,
      dane: activeDane,
    });
  };
  const mutation = useMutation({
    mutationFn: () =>
      createProject(
        {
          opportunity_id: opp.id,
          name: opp.title.slice(0, 200),
        },
        activeDane,
      ),
    onSuccess: (project) => {
      track({
        event_name: "project_created",
        properties: {
          project_id: project.id,
          opportunity_id: opp.id,
          source_code: opp.source.code,
        },
      });
      // El expediente nuevo consume un cupo: sin esto el plan cacheado
      // (staleTime 5min) seguiría diciendo que queda espacio.
      void queryClient.invalidateQueries({ queryKey: myPlanQueryKey(activeDane) });
      // Y ocupa la convocatoria (issue #541): al volver, la tarjeta tiene
      // que decir "Ver expediente", no ofrecer crear otro desde el cache.
      void queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      navigate(`/expedientes/${project.id}`);
    },
    onError: (error) => {
      // 409 = ya hay un expediente vivo de esta convocatoria (pestaña vieja
      // u otro miembro del equipo). Al refrescar, la tarjeta lo enlaza.
      if (error instanceof ApiError && error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      }
      // 402 = el cupo se agotó con el Radar abierto (issue #738). Refrescar
      // el plan bloquea las demás tarjetas y pone el aviso de la página; el
      // listado, por si el expediente que lo agotó salió de esta convocatoria.
      if (error instanceof ApiError && error.status === 402) {
        void queryClient.invalidateQueries({ queryKey: myPlanQueryKey(activeDane) });
        void queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      }
    },
  });

  // `POST /projects` sólo devuelve 402 por cupo de expedientes agotado
  // (`TierGate.guard_new_expediente`), así que el status alcanza para
  // discriminar; el texto lo pone el backend, que ya lo curó.
  const cupoError = mutation.error instanceof ApiError && mutation.error.status === 402;

  // Sprint 19 UX-1 (mayo 21, 2026): el borde resaltado para
  // `is_featured` se eliminó. Sólo se mantiene el estilo "silenciado"
  // para conv descartadas y el ring de selección bulk.
  const baseCardClass = opp.is_dismissed
    ? "flex flex-col rounded-xl border border-slate-200 bg-slate-50 p-5 opacity-70 shadow-xs"
    : "flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-xs";
  const cardClass = isSelected
    ? `${baseCardClass} ring-2 ring-brand-700 ring-offset-2`
    : baseCardClass;

  return (
    <article
      className={cardClass}
      data-tour-id={tourTarget ? "radar-card" : undefined}
      data-testid={opp.is_dismissed ? "opp-card-dismissed" : "opp-card"}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {/* Checkbox de selección bulk — Sprint 9 · 3. Solo se renderiza
            si el padre nos pasó el handler (Radar logueado, sino el
            checkbox no tiene sentido). */}
        {onToggleSelect && (
          <label
            data-testid="card-select-checkbox"
            className="inline-flex cursor-pointer items-center"
            onClick={(e) => {
              // Evita que el click se burbujee a la card si en el futuro
              // alguien hace toda la card clickable.
              e.stopPropagation();
            }}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              aria-label={`Seleccionar ${opp.title}`}
              className="h-4 w-4 cursor-pointer rounded border-slate-300 text-brand-700 focus:ring-2 focus:ring-brand-500"
            />
          </label>
        )}
        {opp.match && (
          <MatchBadge score={opp.match.score} noSignals={opp.match.reasons.length === 0} />
        )}
        <span className="rounded-full bg-brand-50 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-brand-700 uppercase">
          {opp.source.name}
        </span>
        {opp.sector && (
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-slate-700 uppercase">
            {/* La clave interna sin guion bajo: `ciencia_tecnologia` (#751). */}
            {opp.sector.replace(/_/g, " ")}
          </span>
        )}
        <UrlHealthBadge httpStatus={opp.url_http_status} lastCheckedAt={opp.url_last_checked_at} />
        <UrgencyBadge closesAt={opp.closes_at} untilQuotaExhausted={opp.until_quota_exhausted} />
        {/* V2-S6.1: badge de elegibilidad solo en convocatorias internacionales. */}
        {opp.source.origin === "international" && (
          <EligibilityBadge eligibility={opp.source.eligibility} />
        )}
        {/* Acciones del usuario (favoritar / descartar) — Sprint 8.5.
            Movido a V2 (Sprint 10 · 5): se conserva el código pero
            queda detrás de RADAR_CURATION_ACTIONS_ENABLED para que
            cuando se reactive en V2 no haya que reconstruirlo. */}
        {RADAR_CURATION_ACTIONS_ENABLED && activeDane && (
          <div className="ml-auto" data-tour-id={tourTarget ? "card-actions" : undefined}>
            <OpportunityActionButtons
              opportunityId={opp.id}
              isFavorited={opp.is_favorited}
              isDismissed={opp.is_dismissed}
              dane={activeDane}
            />
          </div>
        )}
      </div>

      <h2 className="mb-2 text-base leading-snug font-bold text-slate-900">{opp.title}</h2>

      {opp.description && (
        <p className="mb-3 line-clamp-3 text-sm text-slate-600">{opp.description}</p>
      )}

      {opp.match && opp.match.reasons.length > 0 && (
        <div
          data-testid="match-reasons"
          className="mb-4 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-700"
        >
          <p className="mb-1 font-semibold text-slate-500">Por qué te aplica</p>
          <ul className="list-disc space-y-0.5 pl-4">
            {opp.match.reasons.slice(0, 3).map((reason, idx) => (
              <li key={idx}>{reason}</li>
            ))}
          </ul>
        </div>
      )}

      <dl className="mt-auto grid grid-cols-2 gap-3 border-t border-slate-100 pt-3 text-xs">
        {opp.amount_total_cop !== null && (
          <div>
            <dt className="text-slate-500">Monto total</dt>
            <dd className="font-semibold text-slate-900">{formatCOP(opp.amount_total_cop)}</dd>
          </div>
        )}
        {opp.amount_per_project_max_cop !== null && (
          <div>
            <dt className="text-slate-500">Máx. por proyecto</dt>
            <dd className="font-semibold text-slate-900">
              {formatCOP(opp.amount_per_project_max_cop)}
            </dd>
          </div>
        )}
        {opp.closes_at !== null ? (
          <div data-testid="opp-cierre">
            <dt className="text-slate-500">Cierra</dt>
            <dd className="font-semibold text-slate-900">{formatDate(opp.closes_at)}</dd>
          </div>
        ) : (
          opp.until_quota_exhausted && (
            // Sin fecha de cierre por diseño (#692): se acaba cuando se llenan los cupos.
            <div data-testid="opp-cierre">
              <dt className="text-slate-500">Cierra</dt>
              <dd className="font-semibold text-amber-800">Hasta agotar cupos</dd>
            </div>
          )
        )}
        <div className="text-right">
          <a
            href={opp.source_url}
            target="_blank"
            rel="noreferrer"
            onClick={registrarVista}
            // El clic con la rueda abre otra pestaña sin disparar `click`.
            onAuxClick={(e) => {
              if (e.button === 1) registrarVista();
            }}
            className="text-brand-700 hover:underline"
          >
            Ver original ↗
          </a>
        </div>
      </dl>

      {/* Issue #541: un expediente vivo por convocatoria. Si ya existe, la
          acción de la tarjeta es abrirlo; crear otro lo rechazaría el backend. */}
      {opp.existing_project_id ? (
        <div className="mt-4">
          <p
            data-testid="expediente-existente"
            className="mb-2 flex items-center gap-1.5 text-xs text-slate-600"
          >
            <CircleCheck aria-hidden="true" className="h-4 w-4 shrink-0 text-cta-600" />
            Ya tienes un expediente de esta convocatoria.
          </p>
          <Link
            to={`/expedientes/${opp.existing_project_id}`}
            className="block w-full rounded-md border border-brand-700 bg-white px-3 py-2 text-center text-xs font-semibold text-brand-700 hover:bg-brand-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700"
          >
            Ver expediente
          </Link>
        </div>
      ) : (
        <>
          <button
            type="button"
            disabled={mutation.isPending || cupoAgotado}
            title={
              cupoAgotado
                ? "Tu plan no admite otro expediente activo. Cierra o rechaza uno para liberar un cupo."
                : undefined
            }
            onClick={() => mutation.mutate()}
            data-testid="create-expediente-btn"
            className="mt-4 w-full rounded-md bg-brand-700 px-3 py-2 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
          >
            {mutation.isPending ? "Creando expediente…" : "Crear expediente"}
          </button>
          {/* El 402 del cupo lleva enlace a /membresia; el resto de errores
              se sigue mostrando como una línea suelta. */}
          {cupoError && (
            <CupoExpedientesNota
              className="mt-2"
              tono="error"
              mensaje={(mutation.error as ApiError).message}
            />
          )}
          {mutation.isError && !cupoError && (
            <p className="mt-2 text-xs text-red-700">
              {mutation.error instanceof Error
                ? mutation.error.message
                : "Error al crear expediente"}
            </p>
          )}
        </>
      )}
    </article>
  );
}

function RadarSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-48 animate-pulse rounded-xl border border-slate-200 bg-slate-100"
        />
      ))}
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  // `closes_at` es `date` sin hora: parseo local, no UTC (#537).
  return formatDateEs(iso, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
